import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Tabs,
  Tab,
  Chip,
  useTheme,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import SchoolIcon from '@mui/icons-material/School';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import GroupIcon from '@mui/icons-material/Group';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { getImagePath } from '../utils/imageUtils';

const courses = {
  'Mathematics': [
    {
      title: 'Math Marvels - Mathematics Foundation Course',
      description: 'A fun and engaging journey into the world of numbers, patterns, and logic for students.',
      image: getImagePath('foundation-math.jpg'),
      duration: '8-12 months - 3 classes per week',
      level: 'Beginner to Advanced',
      batchSize: '8-20 students',
      price: '₹ 599- ₹ 1799 per month',
      features: [
        'CBSE/ICSE aligned curriculum',
        'Covers school syllabus',
        'Homework help & doubt solving',
        'Concept reinforcement quizzes',
        'Interactive problem-solving sessions',
        'Quizzes and regular assessments',
        'Enrichment classes and remedial sessions',
      ],
    },
    {
      title: 'Math Wizards - IMO Track',
      description: 'Advanced level preparation for International Mathematics Olympiad (IMO) specially curated for students aspiring to excel in IMO.',
      image: getImagePath('advanced-math.jpg'),
      duration: '8-12 months - 3 classes per week',
      level: 'Advanced',
      batchSize: '8-20 students',
      price: '₹ 599- ₹ 1799 per month',
      features: [
        'IMO level 1 & 2 preparation',
        'Previous Year Questions (PYQs)',
        'Olympiad tricks & strategies',
        'Speed and accuracy techniques',
        'Interactive problem-solving sessions',
        'Mock tests and personal mentoring',
        'Enrichment classes and remedial sessions',
      ],
    },
  ],
  'Science': [
    {
      title: 'Science Explorers - Science Foundation Course',
      description: 'Explore the wonders of Physics, Chemistry & Biology through experiments and quizzes.',
      image: getImagePath('foundation-science.jpg'),
      duration: '8-12 months - 3 classes per week',
      level: 'Beginner to Advanced',
      batchSize: '8-20 students',
      price: '₹ 599- ₹ 1799 per month',
      features: [
        'CBSE/ICSE aligned curriculum',
        'Covers school syllabus',
        'Homework help & doubt solving',
        'Concept reinforcement quizzes',
        'Interactive problem-solving sessions',
        'Quizzes and regular assessments',
        'Enrichment classes and remedial sessions',
      ],
    },
    {
      title: 'Science Champions - ISO Track',
      description: 'Advanced level preparation for International Science Olympiad (ISO) with focus on practical applications.',
      image: getImagePath('advanced-science.jpg'),
      duration: '8-12 months - 3 classes per week',
      level: 'Advanced',
      batchSize: '8-20 students',
      price: '₹ 599- ₹ 1799 per month',
      features: [
        'ISO level 1 & 2 preparation',
        'Previous Year Questions (PYQs)',
        'Olympiad tricks & strategies',
        'Conceptual clarity in science',
        'Interactive problem-solving sessions',
        'Mock tests and personal mentoring',
        'Enrichment classes and remedial sessions',
      ],
    },
  ],
  'English': [
    {
      title: 'English Stars - English Foundation Course',
      description: 'Nurture English grammar, reading, and writing skills with confidence.',
      image: getImagePath('foundation-english.jpg'),
      duration: '8-12 months - 3 classes per week',
      level: 'Beginner to Advanced',
      batchSize: '8-20 students',
      price: '₹ 599- ₹ 1799 per month',
      features: [
        'CBSE/ICSE aligned curriculum',
        'Covers school syllabus',
        'Homework help & doubt solving',
        'Concept reinforcement quizzes',
        'Interactive problem-solving sessions',
        'Quizzes and regular assessments',
        'Enrichment classes and remedial sessions',
      ],
    },
    {
      title: 'English Masters - IEO Track',
      description: 'Advanced level preparation for International English Olympiad (IEO) with focus on communication skills.',
      image: getImagePath('advanced-english.jpg'),
      duration: '8-12 months - 3 classes per week',
      level: 'Advanced',
      batchSize: '8-20 students',
      price: '₹ 599- ₹ 1799 per month',
      features: [
        'IEO level 1 & 2 preparation',
        'Previous Year Questions (PYQs)',
        'Olympiad tricks & strategies',
        'Speed and accuracy techniques',
        'Interactive problem-solving sessions',
        'Mock tests and personal mentoring',
        'Enrichment classes and remedial sessions',
      ],
    },
  ],
  'Social Studies': [
    {
      title: 'Social Studies Foundation Course',
      description: 'Build strong foundation for history, geography, civics and economics.',
      image: getImagePath('social-studies-olympiad.jpg'),
      duration: '8-12 months - 3 classes per week',
      level: 'Beginner to Advanced',
      batchSize: '8-20 students',
      price: '₹ 599- ₹ 1799 per month',
      features: [
        'CBSE/ICSE aligned curriculum',
        'Covers school syllabus',
        'Homework help & doubt solving',
        'Concept reinforcement quizzes',
        'Interactive problem-solving sessions',
        'Quizzes and regular assessments',
        'Enrichment classes and remedial sessions',
      ],
    },
    {
      title: 'Advanced ISSO Training',
      description: 'Advanced level preparation for International Social Studies Olympiad (ISSO) with focus on global awareness.',
      image: getImagePath('social-studies-olympiad.jpg'),
      duration: '8-12 months - 3 classes per week',
      level: 'Advanced',
      batchSize: '8-20 students',
      price: '₹ 599- ₹ 1799 per month',
      features: [
        'ISSO level 1 & 2 preparation',
        'Previous Year Questions (PYQs)',
        'Olympiad tricks & strategies',
        'Speed and accuracy techniques',
        'Interactive problem-solving sessions',
        'Mock tests and personal mentoring',
        'Enrichment classes and remedial sessions',
      ],
    },
  ],
  'Hindi': [
    {
      title: 'Hindi Foundation Course',
      description: 'Build strong foundation for Hindi as a language.',
      image: getImagePath('hindi-olympiad.jpg'),
      duration: '8-12 months - 3 classes per week',
      level: 'Beginner to Advanced',
      batchSize: '8-20 students',
      price: '₹ 599- ₹ 1799 per month',
      features: [
        'CBSE/ICSE aligned curriculum',
        'Covers school syllabus',
        'Homework help & doubt solving',
        'Concept reinforcement quizzes',
        'Interactive problem-solving sessions',
        'Quizzes and regular assessments',
        'Enrichment classes and remedial sessions',
      ],
    },
    {
      title: 'Advanced IHO Training',
      description: 'Advanced level preparation for International Hindi Olympiad (IHO) with focus on Linguistic Proficiency.',
      image: getImagePath('hindi-olympiad.jpg'),
      duration: '8-12 months - 3 classes per week',
      level: 'Advanced',
      batchSize: '8-20 students',
      price: '₹ 599- ₹ 1799 per month',
      features: [
        'IHO level 1 & 2 preparation',
        'Previous Year Questions (PYQs)',
        'Olympiad tricks & strategies',
        'Grammar and Writing techniques',
        'Interactive problem-solving sessions',
        'Mock tests and personal mentoring',
        'Enrichment classes and remedial sessions',
      ],
    },
  ],
};

const Courses = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const theme = useTheme();

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const getTabLabel = (index) => {
    const labels = ['Mathematics', 'Science', 'English','Social Studies','Hindi'];
    return labels[index];
  };

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Typography variant="h3" component="h1" align="center" gutterBottom>
        Regular and Olympiad Preparation Courses
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" align="center" paragraph>
        Regular Foundation Courses and Specialized Olympiad Courses for IMO, ISO, IEO, ISSO and IHO competitions
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          centered
          variant="fullWidth"
          sx={{ mb: 2 }}
        >
          {['Mathematics', 'Science', 'English','Social Studies','Hindi'].map((label, index) => (
            <Tab key={label} label={label} />
          ))}
        </Tabs>
      </Box>

      <Grid container spacing={4}>
        {courses[getTabLabel(selectedTab)].map((course, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'translateY(-8px)',
                },
              }}
            >
              <CardMedia
                component="img"
                height="200"
                image={course.image}
                alt={course.title}
              />
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="h5" component="h2" gutterBottom>
                  {course.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {course.description}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  <Chip
                    icon={<AccessTimeIcon />}
                    label={course.duration}
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    icon={<SchoolIcon />}
                    label={course.level}
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    icon={<GroupIcon />}
                    label={course.batchSize}
                    size="small"
                    variant="outlined"
                  />
                </Box>
                <Typography variant="h6" color="primary" gutterBottom>
                  {course.price}
                </Typography>
                <Box sx={{ mb: 2 }}>
                  {course.features.map((feature, idx) => (
                    <Typography
                      key={idx}
                      variant="body2"
                      color="text.secondary"
                      sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}
                    >
                      • {feature}
                    </Typography>
                  ))}
                </Box>
                <Button
                  component={RouterLink}
                  to="/curriculum-pricing"
                  variant="contained"
                  color="primary"
                  fullWidth
                  startIcon={<EmojiEventsIcon />}
                >
                  Enrol for this course
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

export default Courses; 